// screen: Registered viewer — profile, actions and activity (change viewer-crm)
// Registered viewer — profile (change viewer-crm, design §4.2). Needs viewers:pii (Admin and above); opening
// it is recorded in the viewer audit. Actions need viewers:manage, the data package viewers:export; anonymise and
// delete are confirmed by typing the viewer id.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft } from 'lucide-react';
import type { ApiError, ViewerAction } from '@viewstream/api-client';
import { Badge, Button, Dialog, DialogContent, ErrorState, Input, Skeleton, useToast } from '@viewstream/ui';
import { fmtDateTime, fmtNumber } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/ingest';
import { useExportViewer, useViewer, useViewerAction, useViewerAudit } from '~/app/viewers';
import {
  actorText, useAuditActionLabel, useMethodLabel, usePlatformLabel, usePurposeLabel, useRequestKindLabel, useRequestStatusLabel,
  useStatusLabel, VIEWERS_EXPORT, VIEWERS_MANAGE, VIEWERS_PII,
} from '~/app/ViewersNav';

export const Route = createFileRoute('/_layout/t/$slug/viewers/$viewerId')({ component: Screen });

function Section({ title, children, testid }: { title: string; children: React.ReactNode; testid?: string }) {
  return (
    <section className="rounded-lg border border-border p-4" data-testid={testid}>
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const { viewerId } = Route.useParams();
  const toast = useToast();
  const pii = can(scopes, VIEWERS_PII);
  const v = useViewer(tenant.slug, viewerId);
  const audit = useViewerAudit(tenant.slug, { account_id: viewerId }, pii);
  const act = useViewerAction(tenant.slug, viewerId);
  const exp = useExportViewer(tenant.slug);
  const platform = usePlatformLabel();
  const status = useStatusLabel();
  const method = useMethodLabel();
  const purpose = usePurposeLabel();
  const actionLabel = useAuditActionLabel();
  const kind = useRequestKindLabel();
  const rstatus = useRequestStatusLabel();
  const [confirm, setConfirm] = React.useState<'anonymise' | 'delete' | null>(null);
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => { document.title = appTitle(t`Registered viewer`); }, [t]);
  const dt = (iso: string | null | undefined) => (iso ? fmtDateTime(iso, { locale, tz }) : '—');
  const run = (action: ViewerAction, extra: { device_id?: string; confirm?: string } = {}) =>
    act.mutate({ action, ...extra }, {
      onSuccess: () => toast.push({ title: t`Done` }),
      onError: (e) => toast.push({ title: t`Something went wrong`, description: problemText(e), tone: 'danger' }),
    });
  const back = <Link to={'/t/$slug/viewers' as never} params={{ slug: tenant.slug } as never} className="mb-2 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4 icon-dir" aria-hidden /> {t`Registered viewers`}</Link>;
  if (!pii) return <div className="p-6 text-sm">{back}<p>{t`A viewer's personal data is shown to Admins and Owners only.`}</p></div>;
  if (v.isPending) return <div>{back}<Skeleton className="h-64 w-full" /></div>;
  if (v.isError || !v.data) return <div>{back}<ErrorState error={v.error as ApiError} onRetry={() => v.refetch()} labels={{ title: t`Could not load the viewer`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} /></div>;
  const p = v.data;
  const manage = can(scopes, VIEWERS_MANAGE) && p.status !== 'deleted';
  const hours = (ms: number) => fmtNumber(ms / 3_600_000, locale, { maximumFractionDigits: 1 });

  return (
    <div className="space-y-4" data-testid="viewer-profile">
      {back}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{p.name || <bdi dir="ltr">{p.email}</bdi>}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            <Badge tone={p.status === 'active' ? 'ok' : p.status === 'blocked' ? 'danger' : 'neutral'}>{status(p.status)}</Badge>{' '}
            {t`Joined ${dt(p.created_at)}`} · {t`Last seen ${dt(p.last_seen_at)}`}
          </p>
        </div>
        {manage ? (
          <div className="flex flex-wrap gap-2">
            {p.status === 'active' ? <Button variant="secondary" onClick={() => run('block')} data-testid="viewer-block">{t`Block`}</Button> : null}
            {p.status === 'blocked' ? <Button variant="secondary" onClick={() => run('unblock')}>{t`Unblock`}</Button> : null}
            <Button variant="secondary" onClick={() => run('sign_out')}>{t`Sign out of all devices`}</Button>
            <Button variant="secondary" onClick={() => run('send_reset')} disabled={!p.methods.includes('password')}>{t`Send a password reset`}</Button>
            {can(scopes, VIEWERS_EXPORT) ? (
              <Button variant="secondary" onClick={() => exp.mutate(viewerId, {
                onSuccess: () => toast.push({ title: t`The data package is being prepared`, description: t`Download it from Segments and export → Exports (24 hours, once).` }),
                onError: (e) => toast.push({ title: t`Something went wrong`, description: problemText(e), tone: 'danger' }),
              })}>{t`Export the viewer's data`}</Button>
            ) : null}
            <Button variant="secondary" onClick={() => { setTyped(''); setConfirm('anonymise'); }}>{t`Anonymise…`}</Button>
            <Button variant="danger" onClick={() => { setTyped(''); setConfirm('delete'); }} data-testid="viewer-delete">{t`Delete…`}</Button>
          </div>
        ) : null}
      </div>
      {p.purge_after ? <p className="text-xs text-fg-muted">{t`Deleted automatically if inactive until ${dt(p.purge_after)}.`}</p> : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Section title={t`Details`} testid="viewer-details">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-fg-muted">{t`E-mail`}</dt><dd><bdi dir="ltr">{p.email}</bdi> {p.verified ? <Badge tone="ok">{t`Verified`}</Badge> : <Badge tone="warn">{t`Not verified`}</Badge>}</dd>
            <dt className="text-fg-muted">{t`Sign-in`}</dt><dd>{p.methods.map(method).join(', ') || '—'}</dd>
            <dt className="text-fg-muted">{t`Language`}</dt><dd>{p.lang === 'he' ? 'עברית' : p.lang === 'en' ? 'English' : p.lang || '—'}</dd>
            <dt className="text-fg-muted">{t`Sessions`}</dt><dd>{t`${p.sessions.active} active`} · {t`last ${dt(p.sessions.last)}`}</dd>
          </dl>
        </Section>
        <Section title={t`Consents`} testid="viewer-consents">
          {p.consents.length === 0 ? <p className="text-sm text-fg-muted">{t`No choice recorded yet.`}</p> : (
            <ul className="space-y-1 text-sm">
              {p.consents.map((c) => <li key={c.purpose}>{c.granted ? '✓' : '✗'} {purpose(c.purpose)} · {t`version ${c.text_version}`} · {dt(c.at)}</li>)}
            </ul>
          )}
          {p.consent_history.length > p.consents.length ? (
            <details className="mt-2 text-xs text-fg-muted"><summary>{t`History`}</summary>
              <ul className="mt-1 space-y-0.5">{p.consent_history.map((c, i) => <li key={i}>{c.granted ? '✓' : '✗'} {purpose(c.purpose)} · {c.source} · {dt(c.at)}</li>)}</ul>
            </details>
          ) : null}
        </Section>
      </div>

      <Section title={t`Devices and TV sign-ins`} testid="viewer-devices">
        {p.devices.length === 0 ? <p className="text-sm text-fg-muted">{t`No devices.`}</p> : (
          <ul className="divide-y divide-border text-sm">
            {p.devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-2 py-1.5">
                <span>{platform(d.platform)}{d.name ? ` · ${d.name}` : ''} · {t`since ${dt(d.created_at)}`} · {t`seen ${dt(d.last_seen_at)}`}</span>
                {manage && d.active ? <Button size="sm" variant="ghost" onClick={() => run('sign_out', { device_id: d.id })}>{t`Sign out`}</Button> : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <div className="grid gap-4 md:grid-cols-3">
        <Section title={t`My list (${p.list.length})`}>
          <ul className="space-y-0.5 text-sm">{p.list.slice(0, 20).map((i) => <li key={i.type + i.id}>{i.title || i.id}</li>)}</ul>
        </Section>
        <Section title={t`Continue watching (${p.progress.length})`}>
          <ul className="space-y-0.5 text-sm">{p.progress.slice(0, 20).map((i) => (
            <li key={i.type + i.id}>{i.title || i.id} · <bdi dir="ltr">{Math.round(i.position_s / 60)}/{Math.round(i.duration_s / 60)}</bdi> {t`min`}</li>
          ))}</ul>
        </Section>
        <Section title={t`Viewing in the last 30 days`} testid="viewer-watch">
          {p.watch ? (
            <div className="space-y-1 text-sm">
              <p>{t`${hours(p.watch.watched_ms)} hours · ${p.watch.plays} plays`}</p>
              <p className="text-fg-muted">{t`Live ${hours(p.watch.live_ms)} h · on demand ${hours(p.watch.on_demand_ms)} h`}</p>
              <ul>{p.watch.top_assets.map((a) => <li key={a.id}>{a.title || a.id} · {hours(a.watched_ms)} {t`h`}</li>)}</ul>
            </div>
          ) : <p className="text-sm text-fg-muted">{p.watch_note === 'no_analytics_consent' ? t`No consent to usage analytics.` : t`No viewing data yet.`}</p>}
        </Section>
      </div>

      <Section title={t`Privacy requests`}>
        {p.requests.length === 0 ? <p className="text-sm text-fg-muted">{t`None.`}</p> : (
          <ul className="space-y-1 text-sm">{p.requests.map((r) => <li key={r.id}>{kind(r.kind)} · {rstatus(r.status)} · {t`due ${dt(r.due_at)}`}</li>)}</ul>
        )}
      </Section>

      <Section title={t`Activity log`} testid="viewer-audit">
        <ul className="space-y-1 text-sm">
          {(audit.data?.items ?? []).map((e, i) => (
            <li key={i}>{dt(e.at)} · {actionLabel(e.action)} · <span className="text-fg-muted">{actorText(e.actor, audit.data?.actors, t`The viewer`)}</span></li>
          ))}
        </ul>
      </Section>

      <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent title={confirm === 'delete' ? t`Delete this viewer?` : t`Anonymise this viewer?`} closeLabel={t`Close`}
          description={confirm === 'delete'
            ? t`The account is closed at once and its data, including viewing statistics, is purged within 30 days. This cannot be undone.`
            : t`The e-mail, name and sign-in methods are removed; the list and viewing figures stay without a person behind them. This cannot be undone.`}>
          <p className="mb-2 text-sm">{t`Type the viewer id to confirm:`} <bdi dir="ltr" className="font-mono text-xs">{viewerId}</bdi></p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} dir="ltr" aria-label={t`Viewer id`} data-testid="viewer-confirm-input" />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirm(null)}>{t`Cancel`}</Button>
            <Button variant="danger" disabled={typed.trim() !== viewerId || act.isPending}
              onClick={() => { const a = confirm!; setConfirm(null); run(a, { confirm: viewerId }); }}>
              {confirm === 'delete' ? t`Delete` : t`Anonymise`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
